import { Slot } from "@radix-ui/react-slot";
import { TooltipContentProps } from "@radix-ui/react-tooltip";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../../lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip";

const buttonVariants = cva(
	"inline-flex items-center gap-2 justify-center whitespace-nowrap text-sm font-medium ring-offset-background rounded-md transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:bg-gray-200 disabled:text-gray-500 cursor-pointer max-h-full focus-visible:ring-2 focus-visible:ring-offset-2",
	{
		variants: {
			variant: {
				default:
					"bg-primary text-primary-foreground hover:bg-primary/90 dark:bg-stealth-button dark:hover:bg-stealth-button/90 dark:border dark:border-stealth-divider dark:disabled:bg-stealth-button/50 focus-visible:ring-black dark:focus-visible:bg-stealth-button",
				destructive:
					"bg-destructive text-destructive-foreground disabled:bg-destructive/50 disabled:text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive",
				outline:
					"border border-primary bg-transparent dark:border-white dark:hover:bg-white/5 dark:hover:text-white hover:bg-muted hover:text-muted-foreground disabled:border-transparent disabled:text-gray-300 disabled:bg-transparent dark:focus-visible:ring-white focus-visible:ring-primary/50",
				outlineDestructive:
					"border border-destructive bg-transparent text-destructive dark:border-white disabled:border-transparent disabled:text-gray-300 disabled:bg-transparent dark:focus-visible:ring-white focus-visible:ring-destructive",
				lightOutline:
					"border border-border bg-transparent dark:border-white dark:hover:bg-white/5 dark:hover:text-white hover:bg-accent hover:text-accent-foreground dark:focus-visible:ring-white focus-visible:ring-primary",
				secondary:
					"bg-secondary text-secondary-foreground dark:border dark:border-stealth-divider hover:bg-secondary/80 dark:focus-visible:ring-border focus-visible:ring-secondary",
				lightSecondary: "bg-secondary/15 text-secondary hover:bg-secondary/25",
				ghost: "hover:bg-muted hover:text-muted-foreground disabled:bg-transparent dark:hover:bg-stealth-gradient-blue dark:hover:text-foreground",
				ghostStealth:
					"hover:bg-stealth-tooltip hover:text-white disabled:bg-transparent disabled:text-grey-300",
				ghostSecondary: "hover:bg-muted text-secondary",
				ghostDestructive: "text-destructive hover:bg-destructive/5",
				ghostWarning:
					"text-orange-500 hover:bg-orange-500/5 disabled:bg-transparent disabled:text-gray-500",
				link: "text-primary underline-offset-4 hover:opacity-90",
				linkSecondary: "text-secondary underline-offset-4 hover:opacity-90",
				linkWhite: "text-white underline-offset-4 hover:opacity-90",
				white: "bg-white text-primary hover:bg-white/90",
				black: "bg-black text-white hover:bg-black/90",
				stealthBlue: "bg-stealth-tooltip text-white hover:bg-stealth-blue/90",
				custom: "",
			},
			size: {
				default: "h-10 px-4 py-2",
				xs: "h-8 px-4",
				sm: "h-9 px-3",
				lg: "h-11 px-8",
				icon: "h-8 w-8 rounded-full",
				iconSmall: "h-6 w-6 rounded-full",
			},
			shape: {
				default: "",
				rounded: "rounded-full",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "sm",
			shape: "default",
		},
	},
);

export interface ButtonVariantProps extends VariantProps<typeof buttonVariants> {}
export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		ButtonVariantProps {
	asChild?: boolean;
	tooltip?: string;
	tooltipProps?: TooltipContentProps;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{ className, variant, size, shape, asChild = false, tooltip, tooltipProps = {}, ...props },
		ref,
	) => {
		const Comp = asChild ? Slot : "button";
		const buttonComponent = (
			<Comp
				className={cn(buttonVariants({ variant, size, shape, className }))}
				ref={ref}
				{...props}
			/>
		);

		if (tooltip) {
			return (
				<TooltipProvider>
					<Tooltip delayDuration={100}>
						<TooltipTrigger asChild>{buttonComponent}</TooltipTrigger>
						<TooltipContent {...tooltipProps}>
							<p>{tooltip}</p>
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			);
		}

		return buttonComponent;
	},
);
Button.displayName = "Button";

export { Button, buttonVariants };
